/**
 * The Discount Limits tab's working copy: what the brand has typed but not saved yet, how it is
 * checked, and how it turns into the one request the Save button sends. Pure, so the tab only
 * holds state and wires events.
 */

import { parseDecimal } from '@/lib/decimal';
import {
	DISCOUNT_CODE_MAX_LENGTH,
	DISCOUNT_CODES_MAX,
	DISCOUNT_FREQUENCY_COUNT_MAX,
	type DiscountCodeSource,
	type DiscountExclusions,
	type DiscountSettings
} from '../settings/types';

/** One existing-code field. */
export interface CodeDraft {
	/** Local identity for React keys, so removing a field never hands its DOM node to the next. Never sent. */
	id: string;
	text: string;
}

/** Numbers stay text while typed, so a half-typed "1." or an empty field is not lost. */
export interface DiscountDraft {
	enabled: boolean;
	source: DiscountCodeSource;
	codes: CodeDraft[];
	maxPercent: string;
	maxAmount: string;
	frequencyCount: string;
	frequencyDays: number;
	exclusions: DiscountExclusions;
}

/** The windows the frequency dropdown offers. */
export const FREQUENCY_DAY_OPTIONS = [7, 14, 30, 60, 90] as const;

export const NEEDS_CODE_MESSAGE = 'Add a discount code before switching discounting on.';
export const PERCENT_MESSAGE = 'Enter a percentage above 0 and up to 100, or leave it empty for no maximum.';
export const AMOUNT_MESSAGE = 'Enter an amount above 0, or leave it empty for no cap.';
export const FREQUENCY_MESSAGE = `Enter a whole number from 1 to ${DISCOUNT_FREQUENCY_COUNT_MAX}.`;
export const CODE_LENGTH_MESSAGE = `A code can be at most ${DISCOUNT_CODE_MAX_LENGTH} characters.`;
export const CODES_COUNT_MESSAGE = `List at most ${DISCOUNT_CODES_MAX} codes.`;

/** Which field a problem belongs to, so the tab can show it under that field. */
export type DiscountField = 'codes' | 'maxPercent' | 'maxAmount' | 'frequencyCount';
export type DiscountProblems = Partial<Record<DiscountField, string>>;

let codeSequence = 0;

export const toCodeDraft = (text: string): CodeDraft => {
	codeSequence += 1;
	return { id: `discount-code-${codeSequence}`, text };
};

/** The fields for a saved list: always at least one, empty, to type into. */
export const toCodeDrafts = (codes: string[]): CodeDraft[] => (codes.length ? codes : ['']).map(toCodeDraft);

/** Trimmed, blanks dropped and repeats dropped ignoring case, as Shopify treats codes. */
export const cleanCodes = (codes: CodeDraft[]): string[] => {
	const seen = new Set<string>();
	return codes
		.map((code) => code.text.trim())
		.filter((code) => {
			const key = code.toLowerCase();
			if (!code || seen.has(key)) return false;
			seen.add(key);
			return true;
		});
};

export const toDiscountDraft = (discount: DiscountSettings): DiscountDraft => ({
	enabled: discount.enabled,
	source: discount.source,
	codes: toCodeDrafts(discount.codes),
	maxPercent: discount.maxPercent === null ? '' : String(discount.maxPercent),
	maxAmount: discount.maxAmount === null ? '' : String(discount.maxAmount),
	frequencyCount: String(discount.frequency.count),
	frequencyDays: discount.frequency.days,
	exclusions: discount.exclusions
});

/** Why the listed codes cannot be saved, or null. Only asked while the brand uses its own codes. */
const codesProblem = (draft: DiscountDraft): string | null => {
	const codes = cleanCodes(draft.codes);
	if (codes.some((code) => code.length > DISCOUNT_CODE_MAX_LENGTH)) return CODE_LENGTH_MESSAGE;
	if (codes.length > DISCOUNT_CODES_MAX) return CODES_COUNT_MESSAGE;
	// Switched on with existing codes and none listed, the agent could never give a discount.
	if (draft.enabled && codes.length === 0) return NEEDS_CODE_MESSAGE;
	return null;
};

/** Every problem with the draft, by field; empty when it can be saved. */
export const discountProblems = (draft: DiscountDraft): DiscountProblems => {
	const problems: DiscountProblems = {};
	const percent = parseDecimal(draft.maxPercent);
	if (draft.maxPercent.trim() !== '' && !(percent > 0 && percent <= 100)) problems.maxPercent = PERCENT_MESSAGE;
	if (draft.maxAmount.trim() !== '' && !(parseDecimal(draft.maxAmount) > 0)) problems.maxAmount = AMOUNT_MESSAGE;
	const count = parseDecimal(draft.frequencyCount);
	if (!(Number.isInteger(count) && count >= 1 && count <= DISCOUNT_FREQUENCY_COUNT_MAX)) {
		problems.frequencyCount = FREQUENCY_MESSAGE;
	}
	// The code fields are hidden while codes are generated, so a problem there could not be seen or fixed.
	const codes = draft.source === 'existing' ? codesProblem(draft) : null;
	if (codes) problems.codes = codes;
	return problems;
};

/**
 * The codes a save keeps. While codes are generated the list is hidden but kept for a switch
 * back, so it is cut to what the API accepts rather than refusing a save over fields not shown.
 */
const savedCodes = (draft: DiscountDraft): string[] => {
	const codes = cleanCodes(draft.codes);
	if (draft.source === 'existing') return codes;
	return codes.filter((code) => code.length <= DISCOUNT_CODE_MAX_LENGTH).slice(0, DISCOUNT_CODES_MAX);
};

/** What a save sends. Only call on a draft with no problems. */
export const draftDiscount = (draft: DiscountDraft): DiscountSettings => ({
	enabled: draft.enabled,
	source: draft.source,
	codes: savedCodes(draft),
	maxPercent: draft.maxPercent.trim() === '' ? null : parseDecimal(draft.maxPercent),
	maxAmount: draft.maxAmount.trim() === '' ? null : parseDecimal(draft.maxAmount),
	frequency: { count: parseDecimal(draft.frequencyCount), days: draft.frequencyDays },
	exclusions: draft.exclusions
});

const sameList = (a: string[], b: string[]): boolean => a.length === b.length && a.every((item, i) => item === b[i]);

const sameExclusions = (a: DiscountExclusions, b: DiscountExclusions): boolean =>
	sameList(a.productIds, b.productIds) && sameList(a.tags, b.tags) && sameList(a.collectionIds, b.collectionIds);

/** How a saved number reads in its field: what an untouched draft holds. */
const savedText = (value: number | null): string => (value === null ? '' : String(value));

/**
 * True when the draft would save exactly what is saved: compared as typed, so a half-typed number
 * counts as a change. Compares fields directly, so a render never builds a draft to compare with.
 */
export const sameAsSaved = (draft: DiscountDraft, saved: DiscountSettings): boolean =>
	draft.enabled === saved.enabled &&
	draft.source === saved.source &&
	sameList(cleanCodes(draft.codes), saved.codes) &&
	draft.maxPercent.trim() === savedText(saved.maxPercent) &&
	draft.maxAmount.trim() === savedText(saved.maxAmount) &&
	draft.frequencyCount.trim() === savedText(saved.frequency.count) &&
	draft.frequencyDays === saved.frequency.days &&
	sameExclusions(draft.exclusions, saved.exclusions);

/** True when two saved discounts are the same: how a refetch tells newer settings from an echo. */
export const sameDiscount = (a: DiscountSettings, b: DiscountSettings): boolean =>
	a.enabled === b.enabled &&
	a.source === b.source &&
	sameList(a.codes, b.codes) &&
	a.maxPercent === b.maxPercent &&
	a.maxAmount === b.maxAmount &&
	a.frequency.count === b.frequency.count &&
	a.frequency.days === b.frequency.days &&
	sameExclusions(a.exclusions, b.exclusions);
